import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import AuroraMinPressureLimitControl from './AuroraMinPressureLimitControl'
import { AuroraAssetGroup, AuroraSetMinPressureLimitActionInput } from '../types'
import { useAuroraStack } from '../hooks/aurora'
import { publishAuroraAwhpAction } from '../services/aurora'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'

export interface AuroraAssetGroupMinPressureLimitControlProps {
  auroraAssetGroup: AuroraAssetGroup
  beforeSubmit?: (input: AuroraSetMinPressureLimitActionInput) => void
  afterSubmit?: (value: number) => void
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupMinPressureLimitControl({
  auroraAssetGroup,
  beforeSubmit,
  afterSubmit,
  onError
}: AuroraAssetGroupMinPressureLimitControlProps) {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const {
    auroras,
    minPressureLimit: initialMinPressureLimit,
    handleAuroraAwhpActionError
  } = useAuroraStack(auroraAssetGroup)

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const [minMinPressureLimit, setMinPressureLimit] = useState<number | null>(
    initialMinPressureLimit
  )

  async function handleSubmit(input: AuroraSetMinPressureLimitActionInput) {
    try {
      setSubmitting(true)

      beforeSubmit?.(input)

      await Promise.all(auroras.map((aurora) => publishAuroraAwhpAction(aurora.id, input)))

      dispatch(createSuccessNotification(t('setMinPressureLimitSuccess')))
      setMinPressureLimit(input.alarms.minPressLimit)
      afterSubmit?.(input.alarms.minPressLimit)
    } catch (error) {
      onError?.(error)
      handleAuroraAwhpActionError(error, t('setMinPressureLimitError'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuroraMinPressureLimitControl
      value={minMinPressureLimit}
      loading={isSubmitting}
      onSubmit={handleSubmit}
    />
  )
}
